/**
 * What the reader was reading on the site when they tapped "Start your 25 days free" (core readfrom.ts): …/app/?signup=1&read=lib:<id>
 * or read=blog:<slug>. Kept the moment the app loads, like the level and buy intents, so it survives Google's sign-in window, the email
 * code and a reload; the sign-up screen says "You were reading: <title>". Only an id we know is kept (core readFromParam), for one day.
 * It is only words on the sign-up screen: nothing waits on it and nothing is sent anywhere.
 */
import { READ_PARAM, readFromParam, readFromQuery, readValue, type ReadFrom } from "@logwave/core";

export const READ_KEY = "cc.read.intent";
export const READ_TTL_MS = 24 * 3_600_000;

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
let memory: { read: string; at: string } | null = null;

/** A visit from a guide's or an article's sign-up button: keep what they read. Null for any other visit (or an id we don't know). */
export function captureRead(search: string, storage: Storage | undefined = safeStorage(), now = new Date()): ReadFrom | null {
  const r = readFromQuery(search);
  if (!r) return null;
  memory = { read: readValue(r), at: now.toISOString() };
  try { storage?.setItem(READ_KEY, JSON.stringify(memory)); } catch { /* this page load only */ }
  return r;
}

/** The waiting value, re-checked on every read (a value stored by hand is dropped), or null when none or older than a day. */
export function pendingRead(storage: Storage | undefined = safeStorage(), now = new Date()): ReadFrom | null {
  let v: { read?: unknown; at?: unknown } | null;
  try { v = storage ? JSON.parse(storage.getItem(READ_KEY) ?? "null") : memory; } catch { v = memory; }
  if (!v || typeof v.at !== "string" || typeof v.read !== "string") return null;
  const at = Date.parse(v.at);
  if (!Number.isFinite(at) || now.getTime() - at > READ_TTL_MS || at - now.getTime() > 60_000) return null;
  return readFromParam(v.read);
}

export function clearRead(storage: Storage | undefined = safeStorage()): void {
  memory = null;
  try { storage?.removeItem(READ_KEY); } catch { /* ignore */ }
}

/** The address without the read parameter (signup=1 stays). */
export function withoutReadParam(search: string): string {
  const p = new URLSearchParams(search);
  p.delete(READ_PARAM);
  const s = p.toString();
  return s ? `?${s}` : "";
}
